import {computed, Injectable, signal} from '@angular/core';
import {defer, finalize, interval, Observable, Subscription, switchMap, tap} from 'rxjs';
import {GrafikUmfang} from '../model/event-bus.model';
import {KiChatService, KiStatus} from './http/ki-chat.service';

export type KiLaufZustand = 'laeuft' | 'fertig' | 'fehler';

/** Eine einzelne Anfrage eines Knopfdrucks – im Reiter "Heute" sind es zwei (Heute + Schlagzeile). */
export interface KiTeilLauf {
  /** Die `anfrageId` dieser Anfrage – darüber findet ihre Antwort den richtigen Eintrag. */
  id: string;
  /** Ziel-Textbox ("Heute", "Schlagzeile", …) – erscheint vor der Phase, wenn es mehrere gibt. */
  box: string;
  zustand: KiLaufZustand;
  /** Was gerade im Hintergrund passiert, in Worten. */
  phase: string;
  /** Nur bei `fehler`: die Ursache in Klartext. */
  fehler?: string;
}

/** Alles, was ein Knopfdruck ausgelöst hat. */
export interface KiLauf {
  /** Zeitpunkt des Knopfdrucks (ms). */
  start: number;
  /** Zeitpunkt, an dem die letzte Anfrage endete (ms); fehlt, solange etwas läuft. */
  ende?: number;
  teile: KiTeilLauf[];
}

/**
 * Fortschritt von "KI-Texte erzeugen" – je **gedrücktem Knopf**, für die Anzeige daneben
 * (`KiLaufAnzeigeComponent`): drehender Ring, Sekunden seit dem Klick, die aktuelle Phase,
 * am Ende eine grüne bzw. rote Lampe.
 *
 * Geschlüsselt über `GrafikUmfang.schluessel` des **auslösenden** Knopfes – nicht über den
 * Umfang, an den die Antwort geht: der Knopf an einer Zeile schickt seine Anfrage für den
 * ganzen Reiter (siehe `DocumentDetailComponent.reiterUmfangVon`), die Anzeige soll aber
 * neben genau dem Knopf stehen, der gedrückt wurde. Mehrere Knöpfe → mehrere Anzeigen.
 *
 * **Die Phasen kommen vom AI-Modul.** Jede Anfrage bekommt eine eigene `anfrageId`; das
 * AI-Modul merkt sich darunter, ob gerade das Haupt-Modell schreibt oder das
 * Korrektur-Modell überarbeitet, und solange die Anfrage läuft, wird das hier alle
 * {@link ABFRAGE_MS} ms abgefragt (`KiChatService.status`). Liefert der Server keinen Stand
 * (älterer Rest-Service, andere Instanz), bleibt es bei "wartet auf die KI" – Ring und
 * Sekunden laufen trotzdem.
 *
 * Signal statt Feld aus demselben Grund wie beim `KiErgebnisService`: Auslöser und Anzeige
 * sind verschiedene Ansichten, und alle Änderungen kommen aus HTTP-Callbacks.
 */
@Injectable({providedIn: 'root'})
export class KiLaufService {

  /** Takt der Status-Abfrage während einer laufenden Anfrage. */
  static readonly ABFRAGE_MS = 1500;
  /**
   * Nach so vielen Abfragen ohne jeden Stand wird für diese Anfrage nicht mehr gefragt –
   * dann kennt der Server den Endpunkt offenbar nicht. Wer einmal einen Stand bekam, fragt
   * weiter (ein einzelner Aussetzer ist kein Grund aufzuhören).
   */
  private static readonly MAX_LEERE_ABFRAGEN = 5;

  private readonly laeufe = signal<ReadonlyMap<string, KiLauf>>(new Map());

  /**
   * Die Uhr der Anzeigen: ein **gemeinsamer** Sekundentakt, und nur solange irgendetwas
   * läuft. Ein eigener Takt je Anzeige liefe in jeder Zeile jedes offenen Reiters mit.
   */
  readonly jetzt = signal(Date.now());
  private uhr?: ReturnType<typeof setInterval>;

  /** Läuft gerade irgendeine Anfrage? */
  private readonly etwasLaeuft = computed(() =>
    [...this.laeufe().values()].some(lauf => lauf.ende === undefined && lauf.teile.length > 0));

  constructor(private kiChatService: KiChatService) {
  }

  /** Der Lauf eines Knopfes, oder `undefined`, wenn er noch nie gedrückt wurde. */
  laufFuer(schluessel: string | null | undefined): KiLauf | undefined {
    return schluessel ? this.laeufe().get(schluessel) : undefined;
  }

  /**
   * Ein neuer Knopfdruck: verwirft, was der vorige an diesem Knopf hinterlassen hat
   * (auch die Lampe). Die Anfragen meldet die Aufrufstelle danach mit {@link begleite} an.
   */
  neu(ausloeser: GrafikUmfang): void {
    this.schreibe(ausloeser.schluessel, {start: Date.now(), teile: []});
  }

  /**
   * Begleitet eine Anfrage: legt sie in der Anzeige an, gibt ihr eine `anfrageId`, fragt
   * deren Phase ab, solange sie läuft, und setzt am Ende grün oder rot.
   *
   * @param anfrage baut die eigentliche Anfrage – mit der `anfrageId`, die der Server zum
   *   Melden der Phasen braucht
   * @return dieselbe Anfrage, nur begleitet; Werte und Fehler kommen unverändert durch
   */
  begleite<T>(ausloeser: GrafikUmfang, box: string,
              anfrage: (anfrageId: string) => Observable<T>): Observable<T> {
    const schluessel = ausloeser.schluessel;
    return defer(() => {
      const anfrageId = KiLaufService.neueId();
      this.fuegeTeilHinzu(schluessel, {id: anfrageId, box, zustand: 'laeuft', phase: 'Anfrage wird gesendet …'});
      const abfrage = this.fragePhasenAb(schluessel, anfrageId);
      return anfrage(anfrageId).pipe(
        tap({
          next: antwort => {
            const leer = typeof antwort === 'string' && !antwort.trim();
            this.beende(schluessel, anfrageId, leer ? 'fehler' : 'fertig',
              leer ? 'Die KI hat keinen Text geliefert' : undefined);
          },
          error: fehler => this.beende(schluessel, anfrageId, 'fehler', KiChatService.fehlerUrsache(fehler))
        }),
        finalize(() => abfrage.unsubscribe())
      );
    });
  }

  /** Die Phase in Worten. Unbekanntes bleibt beim allgemeinen "wartet". */
  static phaseInWorten(status: KiStatus | null): string | null {
    switch (status?.phase) {
      case 'ERZEUGEN':
        return 'KI schreibt den Text …';
      case 'KORRIGIEREN':
        switch (status.aufgabe) {
          case 'KUERZEN':
            return 'Text wird gekürzt und überarbeitet …';
          case 'VERLAENGERN':
            return 'Text wird ausformuliert und überarbeitet …';
          default:
            return 'Text wird im Gesamtkontext überarbeitet …';
        }
      case 'FERTIG':
        return 'Text fertig, wird übertragen …';
      default:
        return null;
    }
  }

  private fragePhasenAb(schluessel: string, anfrageId: string): Subscription {
    let leer = 0;
    let gesehen = false;
    const abfrage: Subscription = interval(KiLaufService.ABFRAGE_MS)
      .pipe(switchMap(() => this.kiChatService.status(anfrageId)))
      .subscribe(status => {
        const phase = KiLaufService.phaseInWorten(status);
        if (phase) {
          gesehen = true;
          leer = 0;
          this.setzePhase(schluessel, anfrageId, phase);
          return;
        }
        leer++;
        if (!gesehen && leer === 1) {
          this.setzePhase(schluessel, anfrageId, 'wartet auf die KI …');
        }
        if (!gesehen && leer >= KiLaufService.MAX_LEERE_ABFRAGEN) {
          abfrage.unsubscribe();
        }
      });
    return abfrage;
  }

  private fuegeTeilHinzu(schluessel: string, teil: KiTeilLauf): void {
    const lauf = this.laeufe().get(schluessel) ?? {start: Date.now(), teile: []};
    this.schreibe(schluessel, {start: lauf.start, teile: [...lauf.teile, teil]});
  }

  private setzePhase(schluessel: string, id: string, phase: string): void {
    this.aendereTeil(schluessel, id, teil => teil.zustand === 'laeuft' ? {...teil, phase} : teil);
  }

  private beende(schluessel: string, id: string, zustand: KiLaufZustand, fehler?: string): void {
    this.aendereTeil(schluessel, id, teil => ({...teil, zustand, fehler}));
  }

  private aendereTeil(schluessel: string, id: string, aenderung: (teil: KiTeilLauf) => KiTeilLauf): void {
    const lauf = this.laeufe().get(schluessel);
    if (!lauf?.teile.some(teil => teil.id === id)) {
      return;   // inzwischen neu gedrückt – diese Anfrage gehört nicht mehr zur Anzeige
    }
    const teile = lauf.teile.map(teil => teil.id === id ? aenderung(teil) : teil);
    const fertig = teile.every(teil => teil.zustand !== 'laeuft');
    this.schreibe(schluessel, {start: lauf.start, teile, ende: fertig ? (lauf.ende ?? Date.now()) : undefined});
  }

  private schreibe(schluessel: string, lauf: KiLauf): void {
    const neu = new Map(this.laeufe());
    neu.set(schluessel, lauf);
    this.laeufe.set(neu);
    this.stelleUhr();
  }

  /** Startet bzw. stoppt den gemeinsamen Sekundentakt. */
  private stelleUhr(): void {
    if (this.etwasLaeuft() && !this.uhr) {
      this.jetzt.set(Date.now());
      this.uhr = setInterval(() => this.jetzt.set(Date.now()), 1000);
    } else if (!this.etwasLaeuft() && this.uhr) {
      clearInterval(this.uhr);
      this.uhr = undefined;
      this.jetzt.set(Date.now());
    }
  }

  private static neueId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // crypto.randomUUID gibt es nur in sicheren Kontexten (https, localhost).
    return 'ki-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }
}
